import { gql } from '@apollo/client/core';
import { CRM_STAGE_FIELDS } from '../fragments/crmStage';

const CALL_FIELDS = gql`
  fragment CallFields on CallSummary {
    callId
    conversationId
    assignedAgentId
    assignedAgent {
      agentId
      firstName
      lastName
      email
      phone
      photoUrl
    }
    direction
    inbound
    fromNumber
    toNumber
    status
    outcome
    answeredBy
    durationSeconds
    initiatedAt
    endedAt
    summary
    transcript
    sentiment
    recordingUrl
    agentId
    flowName
    flowType
    callAnalysisTimeline
    callAnalysisAnswers
    isTest
    createdAt
    transfer {
      transferId
      status
      transferReason
      targetAgentNumber
      failureReason
      recordingUrl
      voicemailUrl
      answered
      acceptedBy
    }
  }
`;

export const GET_CONTACT_CALLS = gql`
  ${CALL_FIELDS}
  query GetContactCalls(
    $organizationId: String!
    $contactId: String!
    $limit: Int!
    $offset: Int!
    $dateFrom: DateTime
    $dateTo: DateTime
    $sortBy: String
    $sortOrder: String!
  ) {
    contact(organizationId: $organizationId, contactId: $contactId) {
      contactId
      calls(
        limit: $limit
        offset: $offset
        dateFrom: $dateFrom
        dateTo: $dateTo
        sortBy: $sortBy
        sortOrder: $sortOrder
      ) {
        items {
          ...CallFields
        }
        total
        hasMore
      }
    }
  }
`;

export const GET_DRILL_DOWN_CONTACTS = gql`
  query GetDrillDownContacts(
    $organizationId: String!
    $limit: Int!
    $offset: Int!
    $hasAnsweredCalls: Boolean
    $hasOpenedEmails: Boolean
    $hasClickedEmails: Boolean
    $hasClickedProperties: Boolean
    $hasSearched: Boolean
    $hasWebsiteActivity: Boolean
    $hasConversions: Boolean
    $hasSearchAlerts: Boolean
    $inCallNurture: Boolean
    $inEmailNurture: Boolean
    $isActiveUser: Boolean
    $activityDateFrom: DateTime
    $activityDateTo: DateTime
  ) {
    contacts(
      organizationId: $organizationId
      limit: $limit
      offset: $offset
      hasAnsweredCalls: $hasAnsweredCalls
      hasOpenedEmails: $hasOpenedEmails
      hasClickedEmails: $hasClickedEmails
      hasClickedProperties: $hasClickedProperties
      hasSearched: $hasSearched
      hasWebsiteActivity: $hasWebsiteActivity
      hasConversions: $hasConversions
      hasSearchAlerts: $hasSearchAlerts
      inCallNurture: $inCallNurture
      inEmailNurture: $inEmailNurture
      isActiveUser: $isActiveUser
      activityDateFrom: $activityDateFrom
      activityDateTo: $activityDateTo
    ) {
      contacts {
        contactId
        firstName
        lastName
        email
        phone
        phoneStatus
        leadSource
        leadScore
        optIn
        createdAt
        updatedAt
      }
      total
      hasMore
    }
  }
`;

export const GET_CONTACTS = gql`
  ${CRM_STAGE_FIELDS}
  query GetContacts(
    $organizationId: String!
    $limit: Int!
    $offset: Int!
    $search: String
    $dateFrom: DateTime
    $dateTo: DateTime
    $sortBy: String
    $sortOrder: String!
    $recentOutcomes: [String!]
    $intentLevel: String
    $leadSource: String
    $assignedAgentId: String
    $tag: String
    $tags: [String!]
    $matchAllTags: Boolean
    $flowIds: [String!]
    $flowTypes: [String!]
    $flywheelOptOut: Boolean
    $aiPaused: Boolean
    $optIn: Boolean
    $voiceConsent: String
    $smsConsent: String
    $emailConsent: String
    $phoneStatus: String
    $optInDateFrom: DateTime
    $optInDateTo: DateTime
    $claimedLead: Boolean
    $hasEalerts: Boolean
    $advancedFilter: ContactAdvancedFilterInput
    $loadContacts: Boolean! = true
    $loadTotal: Boolean! = true
  ) {
    contacts(
      organizationId: $organizationId
      limit: $limit
      offset: $offset
      search: $search
      dateFrom: $dateFrom
      dateTo: $dateTo
      sortBy: $sortBy
      sortOrder: $sortOrder
      recentOutcomes: $recentOutcomes
      intentLevel: $intentLevel
      leadSource: $leadSource
      assignedAgentId: $assignedAgentId
      tag: $tag
      tags: $tags
      matchAllTags: $matchAllTags
      flowIds: $flowIds
      flowTypes: $flowTypes
      flywheelOptOut: $flywheelOptOut
      aiPaused: $aiPaused
      optIn: $optIn
      voiceConsent: $voiceConsent
      smsConsent: $smsConsent
      emailConsent: $emailConsent
      phoneStatus: $phoneStatus
      optInDateFrom: $optInDateFrom
      optInDateTo: $optInDateTo
      claimedLead: $claimedLead
      hasEalerts: $hasEalerts
      advancedFilter: $advancedFilter
      loadContacts: $loadContacts
      loadTotal: $loadTotal
    ) {
      contacts @include(if: $loadContacts) {
        contactId
        firstName
        lastName
        email
        phone
        phoneStatus
        emailStatus
        leadSource
        leadSourceSecondary
        leadScore
        leadIntent
        recentOutcome
        assignedAgentId
        assignedAgent {
          agentId
          firstName
          lastName
          email
          phone
          photoUrl
        }
        claimedLead
        tags
        status
        lifecycleStage
        optIn
        optInDate
        consent {
          sms {
            channel
            state
            sourceField
            recordedAt
          }
          email {
            channel
            state
            sourceField
            recordedAt
          }
          call {
            channel
            state
            sourceField
            recordedAt
          }
        }
        callTester
        lastCallAnswered
        hasEalerts
        flywheelOptOut
        aiPaused
        crmStages {
          ...CrmStageFields
        }
        flowExecutions {
          flowName
        }
        allFlowExecutions {
          flowName
        }
        createdAt
      }
      total @include(if: $loadTotal)
      hasMore @include(if: $loadContacts)
    }
  }
`;

export const GET_CLAIMED_CONTACTS = gql`
  query GetClaimedContacts($organizationId: String!, $limit: Int!, $offset: Int!) {
    contacts(
      organizationId: $organizationId
      limit: $limit
      offset: $offset
      sortOrder: "DESC"
      flywheelOptOut: true
      claimedLead: true
    ) {
      contacts {
        contactId
        firstName
        lastName
        email
        phone
        assignedAgentId
        assignedAgent {
          agentId
          firstName
          lastName
          email
          phone
          photoUrl
        }
        claimedLead
      }
      total
      hasMore
    }
  }
`;

export const GET_CONTACT_DETAILS = gql`
  query GetContactDetails($organizationId: String!, $contactId: String!) {
    contact(organizationId: $organizationId, contactId: $contactId) {
      contactId
      firstName
      lastName
      email
      phone
      phoneStatus
      emailStatus
      tags
      optIn
      optInDate
      callTester
      status
      lifecycleStage
      leadSource
      leadScore
      intentLevel
      leadIntent
      recentOutcome
      assignedAgentId
      assignedAgent {
        agentId
        firstName
        lastName
        email
        phone
        photoUrl
      }
      lastCallAnswered
      hasEalerts
      flywheelOptOut
      aiPaused
    }
  }
`;

export const GET_TAGS = gql`
  query GetTags(
    $organizationId: String!
    $limit: Int!
    $offset: Int!
    $search: String
    $isSystemTag: Boolean
    $sortBy: String
    $sortOrder: String!
  ) {
    tags(
      organizationId: $organizationId
      limit: $limit
      offset: $offset
      search: $search
      isSystemTag: $isSystemTag
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      tags {
        tagName
        contactCount
        isSystemTag
        createdAt
      }
      total
      hasMore
    }
  }
`;

// SPEC-2117: one ledger-backed consent event, shared by the Consent tab's
// timeline and by the detail query's per-channel current state. Declared above
// its first use — `const` is not hoisted, so interpolating it into a query
// defined earlier in the file would throw at module load.
const CONSENT_EVENT_FIELDS = gql`
  fragment ConsentEventFields on ConsentEvent {
    consentEventId
    channel
    eventType
    source
    consentState
    actor
    reason
    occurredAt
    createdAt
  }
`;

export const GET_CONTACT = gql`
  ${CONSENT_EVENT_FIELDS}
  ${CRM_STAGE_FIELDS}
  query GetContact($organizationId: String!, $contactId: String!) {
    contact(organizationId: $organizationId, contactId: $contactId) {
      contactId
      firstName
      lastName
      email
      phone
      phoneStatus
      emailStatus
      address {
        street
        city
        state
        zip
      }
      leadSource
      leadSourceSecondary
      leadScore
      intentLevel
      leadIntent
      engagementScore
      status
      lifecycleStage
      recentOutcome
      lastOutcomeChannel
      outcomeHistory
      assignedAgentId
      claimedLead
      crmSource
      crmId
      crmSynced
      crmStages {
        ...CrmStageFields
      }
      crmSyncReason
      crmCreated
      crmLastUpdated
      crmSyncedAt
      lastSyncDirection
      lastSyncTimestamp
      lastSyncedAt
      cadencePath
      activeFlows
      finishedFlows
      activeFlowId
      flywheelEnrolled
      flywheelOptOut
      flywheelOptOutAt
      optIn
      optInDate
      optInDesc
      callTester
      lastCallAnswered
      hasEalerts
      valuationRequested
      websiteActivitySource
      activeHandoffId
      contactHealth
      readinessScore
      readinessBand
      aiPaused
      aiPausedAt
      aiPausedReason
      aiAssistantId
      aiThreadId
      lastMessageAt
      lastMessageFrom
      tags
      customFields
      metadata
      createdAt
      updatedAt
      assignedAgent {
        agentId
        firstName
        lastName
        email
        phone
        role
        status
        photoUrl
      }
      notes {
        items {
          noteId
          subject
          body
          authorId
          authorName
          authorType
          noteType
          isPinned
          category
          tags
          createdAt
          updatedAt
        }
        total
        hasMore
      }
      propertyEnrichment(limit: 1, offset: 0, sortOrder: "DESC") {
        items {
          enrichmentId
          enrichmentStatus
          dataSource
          propertyAddress
          processedData
          rawApiResponse
          confidenceScore
          lastEnrichmentAt
          updatedAt
        }
        total
      }
      suggestedStatus {
        status
        sourceField
        sourceValue
      }
      consent {
        sms {
          channel
          state
          sourceField
          recordedAt
          description
          lastConsentedAt
          lastEvent {
            ...ConsentEventFields
          }
        }
        email {
          channel
          state
          sourceField
          recordedAt
          description
          lastConsentedAt
          lastEvent {
            ...ConsentEventFields
          }
        }
        call {
          channel
          state
          sourceField
          recordedAt
          description
          lastConsentedAt
          lastEvent {
            ...ConsentEventFields
          }
        }
      }
      aiIntelligence {
        summary
        whatRemiKnows
        preferences {
          budgetMin
          budgetMax
          propertyTypes
          locationsOfInterest
          bedroomsMin
          bedroomsMax
          bathroomsMin
          sqftMin
          sqftMax
          mustHaveFeatures
          niceToHaveFeatures
          timeline
          intentLevel
        }
        objections {
          insightType
          insightKey
          description
          updatedAt
        }
        openCommitments {
          insightType
          insightKey
          description
          updatedAt
        }
        nextScheduledTouch {
          channel
          scheduledAt
          reason
        }
      }
      followUpPlan {
        steps {
          stepId
          channel
          when
          title
          purpose
          status
          source
          ealertInterval
        }
        hasActiveFlow
        resolvedOutcome
        resolvedStrategyId
        resolvedOutcomeAt
      }
    }
  }
`;

export const GET_CONTACT_EMAILS = gql`
  query GetContactEmails(
    $organizationId: String!
    $contactId: String!
    $limit: Int!
    $offset: Int!
    $dateFrom: DateTime
    $dateTo: DateTime
    $statuses: [String!]
    $eventTypes: [String!]
    $directions: [String!]
    $sortBy: String!
    $sortOrder: String!
  ) {
    contact(organizationId: $organizationId, contactId: $contactId) {
      contactId
      emails(
        limit: $limit
        offset: $offset
        dateFrom: $dateFrom
        dateTo: $dateTo
        statuses: $statuses
        eventTypes: $eventTypes
        directions: $directions
        latestPerThread: true
        includeScheduledCampaigns: false
        sortBy: $sortBy
        sortOrder: $sortOrder
      ) {
        items {
          messageId
          conversationId
          channel
          direction
          fromAddress
          toAddress
          subject
          body
          bodyHtml
          status
          eventType
          templateName
          campaignId
          campaignStatus
          scheduledTime
          deliveredAt
          openedAt
          createdAt
          sentAt
          emailKind
          replyText
          threadMessageCount
        }
        total
        hasMore
      }
    }
  }
`;

export const GET_CONTACT_SMS = gql`
  query GetContactSms(
    $organizationId: String!
    $contactId: String!
    $limit: Int!
    $offset: Int!
    $dateFrom: DateTime
    $dateTo: DateTime
    $statuses: [String!]
    $sortBy: String!
    $sortOrder: String!
  ) {
    contact(organizationId: $organizationId, contactId: $contactId) {
      contactId
      sms(
        limit: $limit
        offset: $offset
        dateFrom: $dateFrom
        dateTo: $dateTo
        statuses: $statuses
        sortBy: $sortBy
        sortOrder: $sortOrder
      ) {
        items {
          messageId
          conversationId
          channel
          direction
          fromAddress
          toAddress
          body
          status
          deliveredAt
          openedAt
          createdAt
          sentAt
        }
        total
        hasMore
      }
    }
  }
`;

/**
 * Anchored-open: load N messages strictly older than `anchor` and N
 * messages strictly newer. The anchor message itself is NOT in either
 * bunch (per Suleman's PR #2423 contract) — caller renders it from the
 * activity-feed row data it already has.
 *
 * Per Suleman's note: older uses sortOrder DESC so we get the 25 rows
 * CLOSEST to the anchor (not the 25 oldest in the contact's history);
 * the FE reverses to ASC for top-down chat layout. Newer uses ASC
 * directly so we get the 25 rows closest below the anchor first.
 */
export const GET_CONTACT_SMS_AROUND = gql`
  query GetContactSmsAround(
    $organizationId: String!
    $contactId: String!
    $anchor: String!
    $limit: Int!
  ) {
    contact(organizationId: $organizationId, contactId: $contactId) {
      contactId
      older: sms(before: $anchor, limit: $limit, sortBy: "created_at", sortOrder: "DESC") {
        items {
          messageId
          conversationId
          channel
          direction
          fromAddress
          toAddress
          body
          status
          deliveredAt
          openedAt
          createdAt
          sentAt
        }
        hasMore
      }
      newer: sms(after: $anchor, limit: $limit, sortBy: "created_at", sortOrder: "ASC") {
        items {
          messageId
          conversationId
          channel
          direction
          fromAddress
          toAddress
          body
          status
          deliveredAt
          openedAt
          createdAt
          sentAt
        }
        hasMore
      }
    }
  }
`;

export const GET_CONTACT_WEB_ACTIVITY = gql`
  query GetContactWebActivity(
    $organizationId: String!
    $contactId: String!
    $limit: Int!
    $offset: Int!
    $dateFrom: DateTime
    $dateTo: DateTime
    $eventTypes: [String!]
    $sortOrder: String!
  ) {
    contact(organizationId: $organizationId, contactId: $contactId) {
      contactId
      webActivity(
        limit: $limit
        offset: $offset
        dateFrom: $dateFrom
        dateTo: $dateTo
        eventTypes: $eventTypes
        sortOrder: $sortOrder
      ) {
        items {
          activityId
          eventType
          eventCategory
          pageUrl
          deviceType
          properties
          sessionId
          isGuest
          guestInfo
          timestamp
        }
        total
        hasMore
      }
    }
  }
`;

export const GET_CONTACT_WEB_ACTIVITY_STATS = gql`
  query GetContactWebActivityStats(
    $organizationId: String!
    $contactId: String!
    $eventTypes: [String!]
  ) {
    contact(organizationId: $organizationId, contactId: $contactId) {
      contactId
      webActivityStats(eventTypes: $eventTypes) {
        total
        propertyViews
        searches
      }
    }
  }
`;

export const GET_CONTACT_SAVED_PROPERTIES = gql`
  query GetContactSavedProperties(
    $organizationId: String!
    $contactId: String!
    $limit: Int!
    $offset: Int!
    $sortOrder: String!
  ) {
    contact(organizationId: $organizationId, contactId: $contactId) {
      contactId
      savedProperties(limit: $limit, offset: $offset, sortOrder: $sortOrder) {
        items {
          propertyId
          mlsNumber
          mlsBoardCode
          listingStatus
          price
          listPrice
          bedrooms
          bathrooms
          squareFeet
          address
          listingOffice
          primaryPhotoUrl
          features
          isActive
          savedAt
          lastViewedAt
          viewCount
          createdAt
        }
        total
        hasMore
      }
    }
  }
`;

export const GET_CONTACT_VALUATIONS = gql`
  query GetContactValuations(
    $organizationId: String!
    $contactId: String!
    $limit: Int!
    $offset: Int!
    $sortOrder: String!
  ) {
    contact(organizationId: $organizationId, contactId: $contactId) {
      contactId
      valuations(limit: $limit, offset: $offset, sortOrder: $sortOrder) {
        items {
          valuationId
          fullAddress
          addressStreet
          addressCity
          addressState
          addressZip
          estimatedValue
          valueLow
          valueHigh
          confidenceScore
          valuationSource
          bedrooms
          bathrooms
          squareFeet
          lotSize
          yearBuilt
          propertyType
          lookupCount
          isHotLead
          firstLookedUpAt
          lastLookedUpAt
          createdAt
        }
        total
        hasMore
      }
    }
  }
`;

export const GET_CONTACT_PROPERTY_ENRICHMENT = gql`
  query GetContactPropertyEnrichment($organizationId: String!, $contactId: String!) {
    contact(organizationId: $organizationId, contactId: $contactId) {
      contactId
      propertyEnrichment(limit: 1, offset: 0, sortOrder: "DESC") {
        items {
          enrichmentId
          enrichmentType
          enrichmentStatus
          dataSource
          propertyAddress
          processedData
          rawApiResponse
          confidenceScore
          addressMatchQuality
          lastEnrichmentAt
          updatedAt
        }
        total
      }
    }
  }
`;

export const GET_CONTACT_ACTIVITY_FEED = gql`
  query GetContactActivityFeed(
    $organizationId: String!
    $contactId: String!
    $limit: Int!
    $offset: Int!
    $dateFrom: DateTime
    $dateTo: DateTime
    $eventTypes: [String!]
  ) {
    contact(organizationId: $organizationId, contactId: $contactId) {
      contactId
      activityFeed(
        limit: $limit
        offset: $offset
        dateFrom: $dateFrom
        dateTo: $dateTo
        eventTypes: $eventTypes
      ) {
        items {
          eventId
          eventType
          eventSubtype
          channel
          summary
          outcome
          sentiment
          durationSeconds
          callId
          messageId
          sourceSystem
          metadata
          eventAt
          createdAt
          threadMessageCount
        }
        total
        hasMore
      }
    }
  }
`;

export const GET_CONTACT_CONSENT = gql`
  ${CONSENT_EVENT_FIELDS}
  query GetContactConsent(
    $organizationId: String!
    $contactId: String!
    $channel: String
    $dateFrom: DateTime
    $dateTo: DateTime
    $limit: Int
    $offset: Int
  ) {
    contact(organizationId: $organizationId, contactId: $contactId) {
      contactId
      consent {
        sms {
          channel
          state
          sourceField
          recordedAt
          description
          lastConsentedAt
          lastEvent {
            ...ConsentEventFields
          }
        }
        email {
          channel
          state
          sourceField
          recordedAt
          description
          lastConsentedAt
          lastEvent {
            ...ConsentEventFields
          }
        }
        call {
          channel
          state
          sourceField
          recordedAt
          description
          lastConsentedAt
          lastEvent {
            ...ConsentEventFields
          }
        }
      }
      consentHistory(
        channel: $channel
        dateFrom: $dateFrom
        dateTo: $dateTo
        limit: $limit
        offset: $offset
      ) {
        items {
          ...ConsentEventFields
        }
        total
        hasMore
      }
    }
  }
`;

/**
 * Fields the advanced contact filter can reference, with the operators each
 * accepts and the closed value set for enum-valued fields.
 *
 * The filter builder's dropdowns are driven entirely by this query, so the
 * fields on offer are always the fields the resolver can filter on — there is
 * no hand-maintained copy of the catalogue in the frontend.
 */
export const GET_CONTACT_FILTER_FIELDS = gql`
  query GetContactFilterFields($organizationId: String!) {
    contactFilterFields(organizationId: $organizationId) {
      name
      label
      group
      dataType
      operators
      options {
        value
        label
      }
    }
  }
`;
